import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { generateMessageId, useAssistantStore } from '@/stores/useAssistantStore';
import { useCerebroAPI } from '@/hooks/useCerebroAPI';
import { Send, Mic, Square, Loader2, Wand2, Music, Volume2, Command } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const QUICK_ACTIONS = [
  { label: 'Play', icon: Music, action: 'transport.play' },
  { label: 'Record', icon: Square, action: 'transport.record' },
  { label: 'Stop', icon: Square, action: 'transport.stop' },
  { label: 'TTS', icon: Volume2, action: 'tts' },
  { label: 'Command', icon: Command, action: 'command' },
  { label: 'Wand', icon: Wand2, action: 'wand' },
];

function websocketUrl(localAudioBase: string) {
  const base = localAudioBase.replace(/\/$/, '');
  return base.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
}

function pcmS16le(buffer: Float32Array) {
  const pcm = new Int16Array(buffer.length);
  for (let index = 0; index < buffer.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, buffer[index]));
    pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return pcm.buffer;
}

export function ChatInterface() {
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const streamSocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const silentGainRef = useRef<GainNode | null>(null);
  const { messages, isLoading, addMessage, setLoading, localAudioBase } = useAssistantStore();
  const { sendCommand, sendTTS, sendDAW, sendChat, transcribeAudio } = useCerebroAPI();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    const text = input.trim();
    setInput('');

    // Detect intent
    const lower = text.toLowerCase();
    if (lower.startsWith('daw ') || lower.startsWith('play') || lower.startsWith('stop') || lower.startsWith('record')) {
      const action = lower.replace('daw ', '');
      await sendDAW(action);
    } else if (lower.startsWith('say ') || lower.startsWith('tts ')) {
      const voiceText = text.replace(/^(say|tts)\s+/i, '');
      await sendTTS(voiceText);
    } else if (lower.startsWith('command ')) {
      await sendCommand(text.replace(/^command\s+/i, ''));
    } else {
      await sendChat(text);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = async (action: string) => {
    if (action === 'tts') {
      await sendTTS('Cerebro X assistant online.');
    } else if (action === 'command') {
      await sendCommand('Open the DAW and start recording.');
    } else if (action === 'wand') {
      addMessage({
        id: generateMessageId(),
        role: 'assistant',
        content: 'AI Wand activated. What would you like to create?',
        timestamp: new Date().valueOf(),
        metadata: { type: 'chat', status: 'success' }
      });
    } else if (action.startsWith('transport.')) {
      await sendDAW(action);
    }
  };

  const clearRecordingTimeout = () => {
    if (recordingTimeoutRef.current !== null) {
      window.clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  };

  const releaseStreamingNodes = () => {
    audioProcessorRef.current?.disconnect();
    audioSourceRef.current?.disconnect();
    silentGainRef.current?.disconnect();
    audioProcessorRef.current = null;
    audioSourceRef.current = null;
    silentGainRef.current = null;
    const context = audioContextRef.current;
    audioContextRef.current = null;
    if (context && context.state !== 'closed') void context.close();
  };

  const releaseRecorder = () => {
    clearRecordingTimeout();
    releaseStreamingNodes();
    const socket = streamSocketRef.current;
    streamSocketRef.current = null;
    if (socket && socket.readyState === WebSocket.OPEN) socket.close(1000, 'capture_complete');
    recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
    recorderStreamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
  };

  const commitTranscription = (text: string, engine: string) => {
    setInput(text);
    addMessage({
      id: generateMessageId(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
      metadata: { type: 'chat', status: 'success' },
    });
    addMessage({
      id: generateMessageId(),
      role: 'system',
      content: `Local ${engine} transcription complete. Review or press Enter to route this text through the authority command plane.`,
      timestamp: Date.now(),
      metadata: { type: 'system', status: 'success' },
    });
  };

  const transcribeCapturedAudio = async (audio: Blob) => {
    setLoading(true);
    try {
      const transcription = await transcribeAudio(audio);
      commitTranscription(transcription.text, transcription.engine);
    } catch (error) {
      addMessage({
        id: generateMessageId(),
        role: 'system',
        content: `Voice capture failed: ${error instanceof Error ? error.message : 'Unknown local STT error.'}`,
        timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' },
      });
    } finally {
      setLoading(false);
    }
  };

  const stopRecording = () => {
    const socket = streamSocketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      clearRecordingTimeout();
      releaseStreamingNodes();
      recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
      recorderStreamRef.current = null;
      setIsRecording(false);
      setLoading(true);
      socket.send(JSON.stringify({ type: 'stt_stream_stop' }));
      return;
    }
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
    setIsRecording(false);
  };

  const startBatchCapture = (stream: MediaStream) => {
    const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
      .find((candidate) => MediaRecorder.isTypeSupported(candidate));
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    recorderStreamRef.current = stream;
    recorderRef.current = recorder;
    audioChunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      releaseRecorder();
      addMessage({
        id: generateMessageId(), role: 'system', content: 'Microphone capture stopped with a browser error.', timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' },
      });
    };
    recorder.onstop = () => {
      const audio = new Blob(audioChunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
      releaseRecorder();
      void transcribeCapturedAudio(audio);
    };
    recorder.start(250);
    setIsRecording(true);
    addMessage({
      id: generateMessageId(), role: 'system',
      content: 'Voice capture started in batch mode. Stop recording to transcribe locally.', timestamp: Date.now(),
      metadata: { type: 'system', status: 'pending' },
    });
    recordingTimeoutRef.current = window.setTimeout(stopRecording, 120_000);
  };

  const startPcmStream = async (stream: MediaStream) => {
    const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) {
      startBatchCapture(stream);
      return;
    }
    recorderStreamRef.current = stream;
    const socket = new WebSocket(websocketUrl(localAudioBase));
    let streamReady = false;
    let batchFallbackStarted = false;
    const startBatchFallback = () => {
      if (streamReady || batchFallbackStarted) return;
      batchFallbackStarted = true;
      if (streamSocketRef.current === socket) streamSocketRef.current = null;
      startBatchCapture(stream);
    };
    streamSocketRef.current = socket;
    socket.onopen = () => {
      const context = new AudioContextConstructor();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      const silentGain = context.createGain();
      silentGain.gain.value = 0;
      processor.onaudioprocess = (event) => {
        if (socket.readyState !== WebSocket.OPEN || !streamReady) return;
        socket.send(pcmS16le(event.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(silentGain);
      silentGain.connect(context.destination);
      audioContextRef.current = context;
      audioSourceRef.current = source;
      audioProcessorRef.current = processor;
      silentGainRef.current = silentGain;
      void context.resume();
      socket.send(JSON.stringify({ type: 'stt_stream_start', payload: { format: 'pcm_s16le', sampleRate: context.sampleRate, channels: 1, language: 'en' } }));
    };
    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'stt_stream_ready') {
          streamReady = true;
          setIsRecording(true);
          addMessage({
            id: generateMessageId(), role: 'system',
            content: 'Live local voice stream started. Interim recognition uses the docked Whisper runtime.', timestamp: Date.now(),
            metadata: { type: 'system', status: 'pending' },
          });
          recordingTimeoutRef.current = window.setTimeout(stopRecording, 90_000);
        } else if (message.type === 'stt_stream_result') {
          if (message.payload?.isFinal) {
            commitTranscription(message.payload.text, message.payload.engine || 'local-stt');
            releaseRecorder();
            setLoading(false);
          } else if (typeof message.payload?.text === 'string') {
            setInput(message.payload.text);
          }
        } else if (message.type === 'stt_stream_error') {
          addMessage({
            id: generateMessageId(), role: 'system', content: `Live voice stream failed: ${message.payload?.message || 'Unknown STT error.'}`, timestamp: Date.now(),
            metadata: { type: 'system', status: 'error' },
          });
          releaseRecorder();
          setLoading(false);
        }
      } catch {
        addMessage({
          id: generateMessageId(), role: 'system', content: 'The local voice stream returned an invalid message.', timestamp: Date.now(),
          metadata: { type: 'system', status: 'error' },
        });
      }
    };
    socket.onerror = () => startBatchFallback();
    socket.onclose = () => {
      if (!streamReady) startBatchFallback();
    };
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      addMessage({
        id: generateMessageId(), role: 'system',
        content: 'This browser does not provide microphone capture for local STT.', timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' },
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      await startPcmStream(stream);
    } catch (error) {
      releaseRecorder();
      addMessage({
        id: generateMessageId(), role: 'system',
        content: `Microphone access failed: ${error instanceof Error ? error.message : 'Permission was not granted.'}`, timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' },
      });
    }
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else void startRecording();
  };

  useEffect(() => () => {
    if (recordingTimeoutRef.current !== null) window.clearTimeout(recordingTimeoutRef.current);
    audioProcessorRef.current?.disconnect();
    audioSourceRef.current?.disconnect();
    silentGainRef.current?.disconnect();
    const context = audioContextRef.current;
    if (context && context.state !== 'closed') void context.close();
    const socket = streamSocketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) socket.close(1000, 'component_unmounted');
    recorderStreamRef.current?.getTracks().forEach((track) => track.stop());
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  return (
    <div className="flex flex-col h-full bg-void/95 border border-border-subtle rounded-xl overflow-hidden">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        <AnimatePresence>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                  msg.role === 'user'
                    ? 'chat-bubble-user text-primary'
                    : msg.role === 'system'
                    ? 'bg-surface/50 border border-border-subtle text-tertiary text-xs italic'
                    : 'chat-bubble-assistant text-primary'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  {msg.role === 'assistant' && (
                    <span className="text-[10px] uppercase tracking-wider text-violet font-medium">APE_XØRBIT</span>
                  )}
                  {msg.role === 'system' && (
                    <span className="text-[10px] uppercase tracking-wider text-tertiary">SYSTEM</span>
                  )}
                  {msg.metadata?.status === 'pending' && (
                    <Loader2 size={10} className="animate-spin text-gold" />
                  )}
                  {msg.metadata?.status === 'error' && (
                    <span className="text-fire text-[10px]">ERROR</span>
                  )}
                  {msg.metadata?.status === 'success' && msg.role === 'user' && (
                    <span className="text-neon text-[10px]">✓</span>
                  )}
                </div>
                <p className="leading-relaxed">{msg.content}</p>
                <span className="text-[10px] text-tertiary mt-1 block">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="chat-bubble-assistant px-3 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin text-violet" />
                <span className="text-sm text-secondary animate-typing">Processing...</span>
              </div>
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-t border-border-subtle bg-surface/30">
        {QUICK_ACTIONS.map((qa) => (
          <button
            key={qa.action}
            onClick={() => handleQuickAction(qa.action)}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-wider text-tertiary hover:text-primary hover:bg-hover transition-all"
            title={qa.label}
          >
            <qa.icon size={12} />
            <span className="hidden sm:inline">{qa.label}</span>
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-border-subtle bg-surface/50">
        <button
          onClick={toggleRecording}
          className={`p-2 rounded-full transition-all ${
            isRecording
              ? 'bg-fire/20 text-fire animate-pulse-rec'
              : 'text-tertiary hover:text-primary hover:bg-hover'
          }`}
          title={isRecording ? 'Stop recording' : 'Start voice input'}
        >
          <Mic size={16} />
        </button>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Command CEREBRØ_X... (e.g., 'daw play', 'say hello', 'command open mixer')"
          className="flex-1 bg-void border border-border-subtle rounded-lg px-3 py-2 text-sm text-primary placeholder:text-tertiary focus:border-violet focus:outline-none transition-colors"
          disabled={isLoading}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          className="p-2 rounded-full bg-violet/20 text-violet hover:bg-violet/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
